import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaEdit,
  FaArrowRight,
  FaTimes,
  FaWrench,
  FaPlusCircle,
  FaCarSide,
  FaTrash,
  FaFileImport,
  FaChartBar,
  FaUserShield,
  FaUndo,
  FaRedo,
  FaFileAlt,
  FaAngleLeft,
  FaAngleRight,
  FaThumbsUp,
  FaSearchLocation,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
  MDBCheckbox,
  MDBTable,
  MDBTableHead,
  MDBTableBody,
} from "mdb-react-ui-kit";
import { useForm, Controller } from "react-hook-form";

import { supabase } from "../../supabaseClient";
import {
  useManagerAndLocations,
  useSearchConditions,
} from "../../hooks/queries";
import { useSearchSchedules } from "../../hooks/mutation";

import AdminHeaderContainer from "./AdminHeaderContainer";
import ManagerAssignModal from "../modal/ManagerAssign";
import CustomSelect from "../common/select";
import TextInput from "../common/TextInput";
import DateInput from "../common/DateInput";

import "mdb-react-ui-kit/dist/css/mdb.min.css";

const SuperAdminContainer = () => {
  const navigate = useNavigate(); // Use useNavigate instead of useHistory

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [index, setIndex] = useState();
  const [isManagerModalOpen, setManagerModalOpen] = useState(false);
  const [isAssignRideModalOpen, setIsAssignRideModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Fresh-from-Add-Trip IDs that Add Trip stashed in sessionStorage when the
  // manager left that page. We keep them in state so they render at the top
  // with a glow, and clear the stash so refreshing the page doesn't keep
  // re-glowing them forever. Cleared automatically when the user runs any
  // new search (they've clearly moved on).
  const [recentlyAddedIds, setRecentlyAddedIds] = useState(() => {
    try {
      const raw = sessionStorage.getItem("recentlyCreatedTripIds");
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      const ids = Array.isArray(parsed) ? parsed : parsed.ids || [];
      // Consume it so a refresh doesn't re-highlight the same rows.
      sessionStorage.removeItem("recentlyCreatedTripIds");
      return new Set(ids.map((x) => Number(x)).filter(Number.isFinite));
    } catch {
      return new Set();
    }
  });
  const [tempSearch, setTempSearch] = useState("");
  const [page, setPage] = useState(1);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  const { control, handleSubmit, reset, getValues, setValue } = useForm({
    defaultValues: {
      flightNo: "",
      scheduleDate: "",
      passengers: "",
      companyID: undefined,
      airlineID: undefined,
      driverID: undefined,
      vehicleID: undefined,
      locationID: undefined,
      managerID: undefined,
      airlineCode: "",
    },
  });

  const { data: managersAndLocations, isSuccess: successManagersAndLocations } =
    useManagerAndLocations();
  const { data: conditions, isSuccess: successConditions } =
    useSearchConditions();
  
  // Fetch drivers and vehicles when assign modal opens
  useEffect(() => {
    const fetchDriversAndVehicles = async () => {
      if (isAssignRideModalOpen) {
        try {
          // Fetch all drivers for super admin
          const { data: driversData } = await supabase
            .from("Drivers")
            .select("DriverID, FirstName, LastName, UserName, DriverCode");
          
          if (driversData) {
            setDrivers(driversData);
          }
          
          // Fetch vehicles
          const { data: vehiclesData } = await supabase
            .from("Vehicles")
            .select("VehicleID, Code, Make, Model");
          
          if (vehiclesData) {
            setVehicles(vehiclesData);
          }
        } catch (error) {
          console.error("Error fetching drivers and vehicles:", error);
        }
      }
    };
    
    fetchDriversAndVehicles();
  }, [isAssignRideModalOpen]);
  const [companyList, clientList, driverList, vehicleList, locationList, managerFilterList] =
    useMemo(() => {
      if (successConditions) {
        const {
          airlineList,
          vehicleList,
          companyList,
          locationList,
          driverList,
          managerList,
        } = conditions;

        return [
          companyList.map((_item) => ({
            label: _item.Description,
            value: _item.CompanyID,
          })),
          airlineList.map((_item) => ({
            label: _item.Description,
            value: _item.AirlineID,
          })),
          driverList.map((_item) => ({
            label: `${_item.FirstName} ${_item.LastName}`,
            value: _item.DriverID,
          })),
          vehicleList.map((_item) => ({
            label: _item.Model,
            value: _item.VehicleID,
          })),
          locationList.map((_item) => ({
            label: _item.Description,
            value: _item.LocationID,
          })),
          (managerList || []).map((_item) => ({
            label: `${_item.FirstName} ${_item.LastName}`.trim() || _item.Email,
            value: _item.UserID,
          })),
        ];
      } else return [[], [], [], [], [], []];
    }, [conditions, successConditions]);
  const [managersList, locationsList] = useMemo(() => {
    if (successManagersAndLocations) {
      const { managerList, locationList } = managersAndLocations;

      return [
        managerList.map((_manager) => ({
          label: _manager.Email,
          value: _manager.UserID,
        })),
        locationList.map((_location) => ({
          label: _location.Description,
          value: _location.LocationID,
        })),
      ];
    } else {
      return [[], []];
    }
  }, [managersAndLocations, successManagersAndLocations]);

  const onSuccessSearch = useCallback((_data) => {
    setTotalCount(_data.count);
    setSchedules(_data.list);
  }, []);
  const { mutate: searchSchedules, isPending: isSearching } =
    useSearchSchedules(onSuccessSearch);
  // When the manager returns from Add Trip with fresh IDs, kick off an
  // initial search that covers a wide-enough date range to surface the
  // new trips at the top of the list. Runs once on first mount only.
  useEffect(() => {
    if (recentlyAddedIds.size === 0) return;
    // Broad window — Add-Trip rows can be for today, tomorrow, or a
    // whole month out (multi-date select). Query the next 60 days to
    // catch them all in the initial view.
    const today = new Date();
    const in60 = new Date(today.getTime() + 60 * 24 * 3600 * 1000);
    const fmt = (d) => d.toISOString().slice(0, 10);
    searchSchedules({
      startDate: fmt(today),
      endDate: fmt(in60),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(
    (_data) => {
      // Running a fresh search means the manager has moved on from the
      // "look at what I just added" moment — drop the highlight so old
      // Add-Trip glows don't linger on unrelated searches.
      setRecentlyAddedIds(new Set());
      searchSchedules({
        startDate: _data.scheduleDate,
        endDate: _data.scheduleDate,
        driverID: _data.driverID,
        vehicleID: _data.vehicleID,
        airlineID: _data.airlineID,
        companyID: _data.companyID,
        passengers: _data.passengers,
        flightNo: _data.flightNo,
        locationID: _data.locationID,
        managerID: _data.managerID,
        airlineCode: _data.airlineCode,
      });
    },
    [searchSchedules]
  );
  const onClear = useCallback(() => {
    reset();
  }, [reset]);

  const onNextPage = useCallback(() => {
    setPage((old) => {
      const newPage = old + 1;
      const _data = getValues();
      searchSchedules({
        ..._data,
        startDate: _data.scheduleDate,
        endDate: _data.scheduleDate,
        page: newPage,
      });
      return newPage;
    });
  }, [searchSchedules, getValues]);
  const onPrevPage = useCallback(() => {
    setPage((old) => {
      const newPage = old - 1;
      const _data = getValues();
      searchSchedules({
        ..._data,
        startDate: _data.scheduleDate,
        endDate: _data.scheduleDate,
        page: newPage,
      });
      return newPage;
    });
  }, [searchSchedules, getValues]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setValue("airlineCode", tempSearch);
      const _data = getValues();
      searchSchedules({
        ..._data,
        airlineCode: tempSearch,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [tempSearch, setValue, getValues, searchSchedules]);

  const toggleModal = useCallback(() => {
    setIsModalOpen((old) => !old);
  }, []);
  const toggleManagerModal = useCallback(() => {
    setManagerModalOpen((old) => !old);
  }, []);
  const handleAssignRide = useCallback(() => {
    // Check if a schedule is selected before opening the assignment modal
    if (selectedRows.length === 0) {
      toast.error(
        "Please select a schedule/trip first before assigning a driver."
      );
      return;
    }

    // For now, we'll use the first selected schedule
    // In the future, this could be enhanced to handle multiple selections
    const scheduleId = selectedRows[0];
    const schedule = schedules.find((s) => s.ScheduleID === scheduleId);

    if (!schedule) {
      toast.error("Selected schedule not found.");
      return;
    }

    setSelectedScheduleId(scheduleId);
    setSelectedSchedule(schedule);
    setIsAssignRideModalOpen(true);
    // Don't clear selectedRows here - keep the selection visible
  }, [selectedRows, schedules]);

  const handleDelete = useCallback(async () => {
    if (selectedRows.length === 0) {
      toast.error("Select at least one trip to delete.");
      return;
    }
    const ok = window.confirm(
      `Mark ${selectedRows.length} trip${selectedRows.length === 1 ? "" : "s"} as deleted? They stay on record for reports and audit trails but stop appearing on live schedules.`
    );
    if (!ok) return;

    try {
      // Soft-delete — flip ScheduleStatusID to 6 (DELETED) instead of
      // wiping rows. Keeps history intact for reports and logs. Batched
      // into 200 IDs per call so the URL stays under PostgREST's cap.
      const DELETED_STATUS_ID = 6;
      const ids = [...selectedRows];
      const CHUNK = 200;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const batch = ids.slice(i, i + CHUNK);
        const inList = `(${batch.join(",")})`;
        const { error } = await supabase
          .from("Schedule")
          .update({ ScheduleStatusID: DELETED_STATUS_ID })
          .filter("ScheduleID", "in", inList);
        if (error) throw error;
      }

      toast.success(
        `Marked ${selectedRows.length} trip${selectedRows.length === 1 ? "" : "s"} as deleted.`
      );
      setSelectedRows([]);
      // Re-run the current search so the table refreshes without the
      // deleted rows.
      const cur = getValues();
      searchSchedules({
        startDate: cur.scheduleDate,
        endDate: cur.scheduleDate,
        driverID: cur.driverID,
        vehicleID: cur.vehicleID,
        airlineID: cur.airlineID,
        companyID: cur.companyID,
        passengers: cur.passengers,
        flightNo: cur.flightNo,
        locationID: cur.locationID,
        managerID: cur.managerID,
        airlineCode: cur.airlineCode,
      });
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error(`Delete failed: ${err.message || "Unknown error"}`);
    }
  }, [selectedRows, getValues, searchSchedules]);

  const toggleCheckbox = useCallback((id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  }, []);

  const onManagerDetail = useCallback(() => {
    navigate("/manager-detail");
  }, [navigate]);
  const onSuperAdmin = useCallback(() => {
    navigate("/safe-super-admin");
  }, [navigate]);
  const onGenerateQR = useCallback(() => {
    navigate("/qr-codes");
  }, [navigate]);

  const handleReport = useCallback(() => {
    navigate("/reports");
  }, [navigate]);
  const handleFlightStatus = useCallback(() => {
    navigate("/flight-status");
  }, [navigate]);
  const handleScheduleChange = useCallback(() => {
    navigate("/schedule-change");
  }, [navigate]);
  const handleAddNote = useCallback(() => {
    navigate("/add-note");
  }, [navigate]);
  const handleEditTrip = useCallback(() => {
    navigate(`/trip-info/${index}`);
  }, [index, navigate]);
  const handleAddTrip = useCallback(() => {
    navigate("/trip-add");
  }, [navigate]);

  const handleImport = useCallback(() => {
    navigate("/import-data");
  }, [navigate]);
  const handleCharts = useCallback(() => {
    navigate("/charts");
  }, [navigate]);
  const handleDriverInteraction = useCallback(() => {
    navigate("/driver-interaction");
  }, [navigate]);

  useEffect(() => {
    const fetchDrivers = async () => {
      const { data, error } = await supabase
        .from("driver_auth")
        .select(
          `
          id,
          email,
          is_active,
          driver_profiles (
            id,
            first_name,
            last_name,
            phone,
            license_number
          )
        `
        )
        .eq("is_active", true);

      if (error) {
        console.error("Error fetching drivers:", error.message);
      } else {
        // Transform data to match expected format
        const transformedDrivers =
          data?.map((driver) => ({
            DriverID: driver.id,
            DriverCode: driver.driver_profiles?.[0]?.license_number || "",
            UserName:
              `${driver.driver_profiles?.[0]?.first_name || ""} ${
                driver.driver_profiles?.[0]?.last_name || ""
              }`.trim() || driver.email,
            Email: driver.email,
            FirstName: driver.driver_profiles?.[0]?.first_name || "",
            LastName: driver.driver_profiles?.[0]?.last_name || "",
            Phone: driver.driver_profiles?.[0]?.phone || "",
            IsActive: driver.is_active,
          })) || [];

        setDrivers(transformedDrivers);
        setSelectedDriverId(transformedDrivers[0]?.DriverID);
      }
    };
    const fetchVehicles = async () => {
      const { data, error } = await supabase
        .from("Vehicles")
        .select("VehicleID, Code, Make, Model");

      if (error) {
        console.error("Error fetching vehicles:", error.message);
      } else {
        setVehicles(data);
        setSelectedVehicleId(data[0]?.VehicleID);
      }
    };

    fetchDrivers();
    fetchVehicles();
  }, []);
  const handleAssign = useCallback(async () => {
    if (!selectedDriverId || !selectedScheduleId) {
      toast.error(
        "Please select both a driver and ensure a schedule is selected."
      );
      return;
    }

    // Update the schedule's driver_auth_id field instead of creating a Driver_Ride entry
    // This ensures the driver assignment is directly linked to the specific trip/schedule
    const { error } = await supabase
      .from("Schedule")
      .update({
        driver_auth_id: selectedDriverId, // Update the driver_auth_id field
        VehicleID: parseInt(selectedVehicleId), // Also update the vehicle assignment
      })
      .eq("ScheduleID", selectedScheduleId);

    if (error) {
      console.error("Update error:", error);
      toast.error("Failed to assign driver to schedule.");
    } else {
      toast.success("Driver assigned to schedule successfully!");
      // Close modal and reset states
      setIsAssignRideModalOpen(false);
      setSelectedDriverId(null);
      setSelectedVehicleId(null);
      setSelectedScheduleId(null);
      setSelectedSchedule(null);

      // Refresh the schedules list to show the updated assignment
      const _data = getValues();
      searchSchedules({
        ..._data,
        page: page,
      });
    }
  }, [
    selectedDriverId,
    selectedVehicleId,
    selectedScheduleId,
    getValues,
    searchSchedules,
    page,
  ]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      {/* Local styles: highlight glow for freshly-added Add-Trip rows.
          Pulses gently for the first few seconds, then holds a static
          amber outline until the manager runs a new search. */}
      <style>{`
        @keyframes newlyAddedPulse {
          0%   { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.55); }
          60%  { box-shadow: 0 0 0 10px rgba(245, 158, 11, 0);   }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);   }
        }
        tr.newly-added-trip > td {
          background: linear-gradient(90deg, rgba(254, 243, 199, 0.85), rgba(254, 243, 199, 0.35));
          border-top: 1px solid #f59e0b;
          border-bottom: 1px solid #f59e0b;
        }
        tr.newly-added-trip {
          animation: newlyAddedPulse 1.6s ease-out 3;
        }
        tr.newly-added-trip > td:first-child {
          border-left: 3px solid #f59e0b;
        }
      `}</style>
      <div className="p-6 w-full pr-6 pl-6">
        {/* Header */}
        <AdminHeaderContainer
          onChange={(event) => {
            setTempSearch(event.target.value);
          }}
        />

        {/* Flight Details */}
        <div className="flex justify-between gap-4 text-center border-b pb-4 flight-details-container">
          <div className="flex-none col-span-3 col-span-3-layout mb-4">
            <p className="text-xl font-semibold">Quick Overview</p>
            <div
              onClick={onManagerDetail}
              className="justify-between p-3 rounded-lg flex items-center more-detail-button"
            >
              <div>
                <span>All Managers</span>
              </div>
              <div>
                <FaArrowRight />
              </div>
              {/* <button className="mt-3 flex items-center space-x-2 text-blue-600">
                            </button> */}
            </div>
            <div
              onClick={onSuperAdmin}
              className="justify-between p-3 mt-4 rounded-lg flex items-center more-detail-button"
            >
              <div>
                <span>SAFE Super Admin</span>
              </div>
              <div>
                <FaArrowRight />
              </div>
              {/* <button className="mt-3 flex items-center space-x-2 text-blue-600">
                            </button> */}
            </div>
            <div
              onClick={onGenerateQR}
              className="justify-between p-3 mt-4 rounded-lg flex items-center more-detail-button"
            >
              <div>
                <span>Generate QR</span>
              </div>
              <div>
                <FaArrowRight />
              </div>
            </div>
          </div>
          <div className="flex-1 flex justify-between gap-4 flight-details-list">
            <div className="departure-custom-card rounded-lg">
              <p className="text-gray-500">Active drivers</p>
              <p className="text-lg font-semibold">25</p>
            </div>
            <div className="departure-custom-card rounded-lg">
              <p className="text-gray-500">Assigned rides</p>
              <p className="text-lg font-semibold">19</p>
            </div>
            <div className="departure-custom-card rounded-lg">
              <p className="text-gray-500">Available vehicles</p>
              <p className="text-lg font-semibold">6</p>
            </div>
          </div>
        </div>

        {/* Live Driver Update */}
        <div className="driver-content m-4">
          <div className="driver-container">
            <p className="text-location">Driver Roster & Assignment Overview</p>

            <p>
              Easily oversee and manage your fleet with real-time driver
              statuses, assigned vehicles, and upcoming trips. Track current
              locations, update driver profiles, assign vehicles, and ensure
              compliance with safety and documentation requirements—all in one
              streamlined dashboard.
            </p>
          </div>
          <div className="departure-custom-card rounded-lg">
            <Controller
              control={control}
              name="flightNo"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between mb-2">
                  <div>
                    <p>Flight No</p>
                  </div>
                  <div>
                    <TextInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg"
                    />
                  </div>
                </div>
              )}
            />

            <Controller
              control={control}
              name="scheduleDate"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between mb-2">
                  <div>
                    <p>Schedule Date</p>
                  </div>
                  <div>
                    <DateInput
                      value={value}
                      onChange={onChange}
                      className="custom-input rounded-lg"
                    />
                  </div>
                </div>
              )}
            />

            <Controller
              control={control}
              name="companyID"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between mb-2">
                  <div>Company</div>
                  <CustomSelect
                    id="company-select"
                    size="small"
                    options={companyList}
                    value={value}
                    onChange={onChange}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="airlineID"
              render={({ field: { value, onChange } }) => (
                <div className="flex justify-between mb-2">
                  <div>Client</div>
                  <CustomSelect
                    id="client-select"
                    size="small"
                    options={clientList}
                    value={value}
                    onChange={onChange}
                  />
                </div>
              )}
            />

          </div>
          <div className="departure-custom rounded-lg">
            <div className="w-full departure-custom-card rounded-lg">
              <Controller
                control={control}
                name="locationID"
                render={({ field: { value, onChange } }) => (
                  <div className="flex justify-between mb-2">
                    <div>Airport</div>
                    <CustomSelect
                      id="location-select"
                      size="small"
                      options={locationList}
                      value={value}
                      onChange={onChange}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name="passengers"
                render={({ field: { value, onChange } }) => (
                  <div className="flex justify-between mb-2">
                    <div>
                      <p>Passengers</p>
                    </div>
                    <div>
                      <TextInput
                        type="number"
                        value={value}
                        onChange={onChange}
                        className="custom-input rounded-lg"
                      />
                    </div>
                  </div>
                )}
              />

              <Controller
                control={control}
                name="driverID"
                render={({ field: { value, onChange } }) => (
                  <div className="flex justify-between mb-2">
                    <div>Driver</div>
                    <CustomSelect
                      id="driver-select"
                      size="small"
                      options={driverList}
                      value={value}
                      onChange={onChange}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name="vehicleID"
                render={({ field: { value, onChange } }) => (
                  <div className="flex justify-between mb-2">
                    <div>Vehicle</div>
                    <CustomSelect
                      id="vehicle-select"
                      size="small"
                      options={vehicleList}
                      value={value}
                      onChange={onChange}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name="managerID"
                render={({ field: { value, onChange } }) => (
                  <div className="flex justify-between mb-2">
                    <div>Manager</div>
                    <CustomSelect
                      id="manager-filter-select"
                      size="small"
                      options={managerFilterList}
                      value={value}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>

            <div className="flex justify-between mb-2">
              <button
                onClick={handleSubmit(handleSearch)}
                disabled={isSearching}
                className="justify-between p-2 pr-20 pl-20 rounded-lg flex items-center custom-button"
              >
                <span>Search</span>
              </button>
              <div>
                <div
                  onClick={onClear}
                  className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
                >
                  <span>Clear</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="manger-button-groups-container p-2 rounded-lg mb-4">
          <div className="flex align-center cursor-pointer">
            <FaWrench />
            <p className="mb-0 pl-10">Repair</p>
          </div>
          <div
            onClick={handleDriverInteraction}
            className="flex align-center cursor-pointer"
          >
            <FaThumbsUp />
            <p className="mb-0 pl-10">Driver Interaction</p>
          </div>
          <div
            onClick={toggleManagerModal}
            className="flex align-center cursor-pointer"
          >
            <FaSearchLocation />
            <p className="mb-0 pl-10">Manager Assign</p>
          </div>
          <div
            onClick={handleAddTrip}
            className="flex align-center cursor-pointer pl-10"
          >
            <FaPlusCircle />
            <p className="mb-0 pl-10">Add Trip</p>
          </div>

          <div
            onClick={handleAssignRide}
            className="flex align-center cursor-pointer pl-10"
          >
            <FaCarSide />
            <p className="mb-0 pl-10">Assign Ride</p>
          </div>

          <div
            onClick={handleDelete}
            className="flex align-center cursor-pointer pl-10"
          >
            <FaTrash />
            <p className="mb-0 pl-10">Delete</p>
          </div>

          <div
            onClick={handleImport}
            className="flex align-center cursor-pointer pl-10"
          >
            <FaFileImport />
            <p className="mb-0 pl-10">Import</p>
          </div>

          <div
            onClick={handleCharts}
            className="flex align-center cursor-pointer pl-10"
          >
            <FaChartBar />
            <p className="mb-0 pl-10">Charts</p>
          </div>

          <div className="flex align-center cursor-pointer pl-10">
            <FaUserShield />
            <p className="mb-0 pl-10">Admin</p>
          </div>

          <div className="flex align-center cursor-pointer pl-10">
            <FaUndo />
            <p className="mb-0 pl-10">Undo</p>
          </div>

          <div
            onClick={handleReport}
            className="flex align-center cursor-pointer pl-10"
          >
            <FaFileAlt />
            <p className="mb-0 pl-10">Report</p>
          </div>

          <div className="flex align-center cursor-pointer pl-10">
            <FaRedo />
            <p className="mb-0 pl-10">Refresh</p>
          </div>
        </div>

        <div className="mt-4 mr-0 ml-0" id="schedule-table">
          <div className="table-responsive">
            <MDBTable striped bordered hover>
              <MDBTableHead
                className="text-white"
                style={{
                  background: "linear-gradient(135deg, #1a56a8, #0A7AFF)",
                  color: "#fff",
                }}
              >
                <tr>
                  <th className="p-10">
                    <MDBCheckbox
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRows(schedules.map((row) => row.ScheduleID));
                        } else {
                          setSelectedRows([]);
                        }
                      }}
                      checked={selectedRows.length === schedules.length}
                    />
                  </th>
                  <th className="p-10 text-center">ID</th>
                  <th className="p-10 text-center">Schedule Date (ET)</th>
                  <th className="p-10 text-center">Airline</th>
                  <th className="p-10 text-center">Flight No</th>
                  <th className="p-10 text-center">Status</th>
                  <th className="p-10 text-center">Transport</th>
                  <th className="p-10 text-center">Driver</th>
                  <th className="p-10 text-center">Vehicle</th>
                  <th className="p-10 text-center">Pilots</th>
                  <th className="p-10 text-center">F/A</th>
                  <th className="p-10 text-center">Pass</th>
                  <th className="p-10 text-center">P/U</th>
                  <th className="p-10 text-center">D/O</th>
                  <th className="p-10 text-center">Edit</th>
                  <th className="p-10 text-center">Delay</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {[...schedules]
                  // Freshly created Add-Trip rows float to the top with a
                  // temporary glow so the manager can visually confirm what
                  // just got added. Any other ordering (date, ID) is
                  // preserved below the highlighted band.
                  .sort((a, b) => {
                    const aNew = recentlyAddedIds.has(Number(a.ScheduleID)) ? 1 : 0;
                    const bNew = recentlyAddedIds.has(Number(b.ScheduleID)) ? 1 : 0;
                    return bNew - aNew;
                  })
                  .map((row) => {
                  const isDeleted = (row.Status || "").toUpperCase() === "DELETED";
                  const isNewlyAdded = recentlyAddedIds.has(Number(row.ScheduleID));
                  // Deleted trips stay in the search view but read as
                  // struck-through/faded so they're obviously non-active
                  // while remaining auditable.
                  const rowStyle = isDeleted
                    ? {
                        opacity: 0.55,
                        textDecoration: "line-through",
                        color: "#6b7280",
                      }
                    : undefined;
                  const rowClass = isNewlyAdded
                    ? "p-10 newly-added-trip"
                    : "p-10";
                  return (
                  <tr key={row.ScheduleID} className={rowClass} style={rowStyle}>
                    <td className="p-10">
                      <MDBCheckbox
                        onChange={() => toggleCheckbox(row.ScheduleID)}
                        checked={selectedRows.includes(row.ScheduleID)}
                      />
                    </td>
                    <td className="p-10">{row.ScheduleID}</td>
                    <td className="p-10">{row.ScheduleDate}</td>
                    <td className="p-10">{row.Airline}</td>
                    <td className="p-10">
                      <a
                        href={`https://www.flightaware.com/live/flight/${
                          row.FlightNo.split("-")[0]
                        }`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {row.FlightNo}
                      </a>
                    </td>
                    <td className="p-10">
                      {isDeleted ? (
                        <span style={{ color: "#b91c1c", fontWeight: 600 }}>
                          DELETED
                        </span>
                      ) : (
                        row.Status
                      )}
                    </td>
                    <td className="p-10">{row.Transport}</td>
                    <td className="p-10">{row.Driver}</td>
                    <td className="p-10">{row.Vehicle}</td>
                    <td className="p-10">{row.Pilots}</td>
                    <td className="p-10">{row.FA}</td>
                    <td className="p-10">{row.Pass}</td>
                    <td className="p-10">{row.PU}</td>
                    <td className="p-10">{row.DO}</td>
                    <td
                      onClick={() => {
                        setIndex(row.ScheduleID);
                        toggleModal();
                      }}
                      className="cursor-pointer flex justify-center"
                    >
                      <FaEdit />
                    </td>
                    <td className="p-10">On-Time</td>

                    {/* <td>{row.d}</td> */}
                  </tr>
                  );
                })}
              </MDBTableBody>
            </MDBTable>
          </div>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * 10 + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * 10)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page == 1 || isSearching}
                    className={`pagination-item ${page == 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * 10 > totalCount || isSearching}
                    className={`pagination-item ${
                      page * 10 > totalCount ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="modal-container h-auto w-50">
              <div className="modal-header mb-0">
                {/* <h2 className="text-xl font-semibold">Driver & Vehicle Information</h2> */}
                <button className="modal-close" onClick={toggleModal}>
                  <FaTimes size={20} />
                </button>
              </div>
              <div className="grid justify-between grid-cols-2 gap-4">
                {/* <h1>Manage Flight Assignment</h1> */}
                <p className="text-lg font-semibold">
                  Manage Flight Assignment
                </p>
                <p className="text-gray-500">
                  Update flight details, adjust schedules, add notes, or make
                  edits as needed.
                </p>
                <div
                  onClick={handleFlightStatus}
                  className="bg-gray-200 cursor-pointer flex justify-between p-4 rounded-lg items-center mt-4 mb-4"
                >
                  <div>
                    <span>Flight Status</span>
                  </div>
                  <div>
                    <FaArrowRight />
                  </div>
                </div>
                <div
                  onClick={handleScheduleChange}
                  className="bg-gray-200 cursor-pointer flex justify-between p-4 rounded-lg items-center mt-4 mb-4"
                >
                  <div>
                    <span>Schedule Change</span>
                  </div>
                  <div>
                    <FaArrowRight />
                  </div>
                </div>

                <div
                  onClick={handleAddNote}
                  className="bg-gray-200 flex justify-between p-4 rounded-lg items-center mt-4 mb-4"
                >
                  <div>
                    <span>Add Note</span>
                  </div>
                  <div>
                    <FaArrowRight />
                  </div>
                </div>
                <div
                  onClick={handleEditTrip}
                  className="bg-gray-200 flex justify-between p-4 rounded-lg items-center mt-4 mb-4"
                >
                  <div>
                    <span>Edit</span>
                  </div>
                  <div>
                    <FaArrowRight />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <ManagerAssignModal
          isOpen={isManagerModalOpen}
          onClose={toggleManagerModal}
          managersList={managersList}
          locationsList={locationsList}
        />
        {isAssignRideModalOpen && (
          <div className="modal-overlay">
            <div className="modal-container h-auto w-50">
              <div className="modal-header mb-0">
                {/* <h2 className="text-xl font-semibold">Driver & Vehicle Information</h2> */}
                <button
                  className="modal-close"
                  onClick={() => setIsAssignRideModalOpen(false)}
                >
                  <FaTimes size={20} />
                </button>
              </div>
              <div className="grid justify-between grid-cols-2 gap-4">
                <p className="text-lg font-semibold">
                  Assign Driver to Schedule
                </p>

                {/* Display selected schedule information */}
                {selectedSchedule && (
                  <div className="col-span-2 mb-4 p-3 bg-gray-100 rounded-lg">
                    <p className="font-semibold text-sm text-gray-700">
                      Selected Schedule:
                    </p>
                    <p className="text-sm text-gray-600">
                      Flight: {selectedSchedule.FlightNo} | Date:{" "}
                      {selectedSchedule.ScheduleDate} | Airline:{" "}
                      {selectedSchedule.Airline}
                    </p>
                  </div>
                )}
                <div className="cursor-pointer flex justify-between pt-4 ">
                  <div>
                    <span>Driver</span>
                  </div>
                  <div>
                    <select
                      className="custom-input rounded-lg"
                      value={selectedDriverId ?? ""}
                      onChange={(e) => setSelectedDriverId(e.target.value)}
                    >
                      <option value="">Select Driver</option>
                      {drivers.map((driver) => (
                        <option key={driver.DriverID} value={driver.DriverID}>
                          {driver.FirstName} {driver.LastName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="cursor-pointer flex justify-between pt-4">
                  <div>
                    <span>Car</span>
                  </div>
                  <div>
                    <select
                      className="custom-input rounded-lg"
                      value={selectedVehicleId ?? ""}
                      onChange={(e) => setSelectedVehicleId(e.target.value)}
                    >
                      <option value="">Select Vehicle</option>
                      {vehicles.map((vehicle) => (
                        <option
                          key={vehicle.VehicleID}
                          value={vehicle.VehicleID}
                        >
                          {vehicle.Code} - {vehicle.Make} {vehicle.Model}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-between mt-4 save-button">
                  <div
                    onClick={handleAssign}
                    className="justify-center p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
                  >
                    <span>Assign</span>
                  </div>
                  <div
                    onClick={() => {
                      setIsAssignRideModalOpen(false);
                      setSelectedScheduleId(null);
                      setSelectedSchedule(null);
                      setSelectedDriverId(null);
                      setSelectedVehicleId(null);
                    }}
                    className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
                  >
                    <span>Cancel</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminContainer;
